import React, { useState, useEffect } from "react";
import http from "../Utils/axiosConfig";
import { onlyNumber, toISODate } from "../Utils/commonFuntion";
import "./DairySales.css";

const today = toISODate();
const fmt   = (v) => v != null ? Number(v).toFixed(2) : "-";
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";

const INIT = { date: today, session: "morning", type: "buffalo", milk: "", rate_per_ltr: "", total_rate: "" };

const DairySales = () => {
  const [form, setForm]         = useState(INIT);
  const [loading, setLoading]   = useState(false);
  const [success, setSuccess]   = useState(false);
  const [errors, setErrors]     = useState({});

  const [rows, setRows]         = useState([]);
  const [page, setPage]         = useState(1);
  const [pagination, setPagination] = useState(null);
  const [fetching, setFetching] = useState(false);

  // Auto-calc total
  useEffect(() => {
    const m = parseFloat(form.milk);
    const r = parseFloat(form.rate_per_ltr);
    if (m > 0 && r > 0) {
      setForm(prev => ({ ...prev, total_rate: (m * r).toFixed(2) }));
    }
  }, [form.milk, form.rate_per_ltr]);

  // Fetch on page change
  useEffect(() => { fetchRecords(page); }, [page]);

  const fetchRecords = async (p) => {
    setFetching(true);
    try {
      const res = await http.post("dairy-sales/list", { page: p });
      if (res?.code === "DATA_FOUND") {
        setRows(res.data.records);
        setPagination(res.data.pagination);
      } else {
        setRows([]);
        setPagination(null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: "" }));
    setSuccess(false);
  };

  const validate = () => {
    const e = {};
    if (!form.date)         e.date         = "तारीख आवश्यक आहे";
    if (!form.milk)         e.milk         = "दूध आवश्यक आहे";
    if (!form.rate_per_ltr) e.rate_per_ltr = "दर आवश्यक आहे";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await http.post("dairy-sales/add", form);
      if (res?.code === "ENTRY_ADDED") {
        setSuccess(true);
        setForm(INIT);
        // Refresh first page
        setPage(1);
        fetchRecords(1);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ds-container">

      {/* ── Form ── */}
      <form className="ds-form" onSubmit={handleSubmit}>

        {/* Date */}
        <div className="ds-field">
          <input type="date" name="date" value={form.date}
            onChange={handleChange} className="ds-date-input" />
          {errors.date && <p className="ds-error">{errors.date}</p>}
        </div>

        {/* Session */}
        <div className="ds-field ds-radio-row">
          <label className="ds-label">वेळ</label>
          <div className="ds-radios">
            {[["morning","सकाळ"],["evening","संध्याकाळ"]].map(([v,l]) => (
              <label key={v} className="ds-radio-label">
                <input type="radio" name="session" value={v}
                  checked={form.session === v} onChange={handleChange} />
                {l}
              </label>
            ))}
          </div>
        </div>

        {/* Type */}
        <div className="ds-field ds-radio-row">
          <label className="ds-label">प्रकार</label>
          <div className="ds-radios">
            {[["buffalo","म्हैस"],["cow","गाय"]].map(([v,l]) => (
              <label key={v} className="ds-radio-label">
                <input type="radio" name="type" value={v}
                  checked={form.type === v} onChange={handleChange} />
                {l}
              </label>
            ))}
          </div>
        </div>

        {/* Milk */}
        <div className="ds-field ds-inline">
          <label className="ds-label">दूध:</label>
          <input type="number" name="milk" value={form.milk}
            onChange={handleChange} onKeyDown={onlyNumber}
            step="any" placeholder="लिटर" className="ds-input" />
          {errors.milk && <p className="ds-error">{errors.milk}</p>}
        </div>

        {/* Rate */}
        <div className="ds-field ds-inline">
          <label className="ds-label">दर/लिटर:</label>
          <input type="number" name="rate_per_ltr" value={form.rate_per_ltr}
            onChange={handleChange} onKeyDown={onlyNumber}
            step="any" className="ds-input" />
          {errors.rate_per_ltr && <p className="ds-error">{errors.rate_per_ltr}</p>}
        </div>

        {/* Total rate — readonly auto-calc */}
        <div className="ds-field ds-inline">
          <label className="ds-label">एकूण दर:</label>
          <input type="number" name="total_rate" value={form.total_rate}
            readOnly className="ds-input ds-input--readonly" />
        </div>

        {success && <p className="ds-success">✅ नोंद जतन झाली!</p>}

        <div className="ds-submit-row">
          <button type="submit" className="ds-submit-btn" disabled={loading}>
            {loading ? "जतन होत आहे..." : "OKAY"}
          </button>
        </div>
      </form>

      {/* ── Table ── */}
      <div className="ds-table-section">
        <div className="ds-table-wrap">
          {fetching && rows.length === 0 ? (
            <div className="ds-no-data"><p>लोड होत आहे...</p></div>
          ) : rows.length === 0 ? (
            <div className="ds-no-data"><p>कोणत्याही नोंदी उपलब्ध नाहीत.</p></div>
          ) : (
            <table className="ds-table">
              <thead>
                <tr>
                  <th>तारीख</th>
                  <th>वेळ</th>
                  <th>प्रकार</th>
                  <th>दूध (लि.)</th>
                  <th>दर/लि.</th>
                  <th>एकूण दर (₹)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>{fmtDate(r.date)}</td>
                    <td>{r.session === "morning" ? "सकाळ" : "संध्याकाळ"}</td>
                    <td>{r.type === "buffalo" ? "म्हैस" : "गाय"}</td>
                    <td>{fmt(r.milk)}</td>
                    <td>{fmt(r.rate_per_ltr)}</td>
                    <td className="ds-amount">{fmt(r.total_rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="ds-pagination">
            <button
              className="ds-page-btn"
              onClick={() => setPage(p => p - 1)}
              disabled={page === 1 || fetching}
            >
              ‹ मागे
            </button>

            <div className="ds-page-numbers">
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === pagination.totalPages || Math.abs(p - page) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === "..." ? (
                    <span key={i} className="ds-page-ellipsis">…</span>
                  ) : (
                    <button
                      key={p}
                      className={`ds-page-num ${page === p ? "ds-page-num--active" : ""}`}
                      onClick={() => setPage(p)}
                      disabled={fetching}
                    >
                      {p}
                    </button>
                  )
                )}
            </div>

            <button
              className="ds-page-btn"
              onClick={() => setPage(p => p + 1)}
              disabled={page === pagination.totalPages || fetching}
            >
              पुढे ›
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DairySales;
